import * as CSV from 'csv-string';
import { action, computed, observable } from 'mobx';
import moment from 'moment';

import { Paginated } from '../../adl-gen/common';
import { DbKey, WithDbId } from '../../adl-gen/common/db';
import { TableQuery } from '../../adl-gen/common/tabular';
import {
  AppClient,
  makeUserUploadFields,
  makeUserUploadRecord,
  makeUserUploadSet,
  UserUploadFields,
  UserUploadRecord,
  UserUploadSet
} from '../../adl-gen/totalinteraction/futurefit/db';
import { UserUploadData, UserUploadResult } from '../../adl-gen/totalinteraction/futurefit/requests';
// import { LangCode } from "../../internationalization/i18n-hoc";
import { fromPromise, MaybeLoading } from '../../loader/loader';
import { Service } from '../../service/service';
import { LoggedIn, LoggedInSuper } from '../../stores/identity-store';
import { ListFieldInfo } from '../ui/common/list/list-types';
import { FileContent } from '../ui/components/dropzone/dropzone';

import { DataStore, TransportLayer } from './datastore';

export type CSVField = Pick<ListFieldInfo<UserUploadData, unknown>, 'name' | 'label'>;

class AppUserUploadStoreApi implements TransportLayer<UserUploadRecord> {
  kind: string = 'UserUploadRecord';
  constructor(private readonly service: Service) {}

  create = async (record: UserUploadRecord): Promise<DbKey<UserUploadRecord>> =>
    this.service.createUserUploadRecord(record);
  update = async (record: WithDbId<UserUploadRecord>): Promise<void> => this.service.updateUserUploadRecord(record);
  doDelete = async (id: DbKey<UserUploadRecord>): Promise<void> => this.service.deleteUserUploadRecord(id);
  query = async (query: TableQuery): Promise<Paginated<WithDbId<UserUploadRecord>>> =>
    this.service.queryUserUploadRecords(query);

  queryAppClient = async (query: TableQuery): Promise<Paginated<WithDbId<AppClient>>> =>
    this.service.queryAppClients(query);
  processUpload = async (data: UserUploadData): Promise<UserUploadResult> => this.service.processUserUpload(data);
}

export class AppUserUploadStore extends DataStore<UserUploadRecord> {
  @observable appClient: Promise<WithDbId<AppClient>>;
  @observable userUploadSet?: UserUploadSet;
  @observable userUploadResult?: Promise<UserUploadResult>;
  public userUploadFields: UserUploadFields = makeUserUploadFields({});
  @observable fields: CSVField[] = [];

  constructor(
    readonly transport: AppUserUploadStoreApi,
    readonly identity: LoggedIn | LoggedInSuper,
    readonly appClientId: DbKey<AppClient>,
    readonly setId?: DbKey<UserUploadSet>
  ) {
    super(transport, identity);
    // TODO: load set
    this.appClient = this.loadClient(appClientId);
    this.fields = Object.values(this.userUploadFields).map((importField: string) => {
      const name = importField; //.toLowerCase().replace(/\W/g, "_")
      const label = importField.toUpperCase();
      return { name, label };
    });
  }

  static fromService = (
    service: Service,
    identity: LoggedIn | LoggedInSuper,
    clientId: DbKey<AppClient>,
    setId?: DbKey<UserUploadSet>
  ) => {
    const store = new AppUserUploadStore(new AppUserUploadStoreApi(service), identity, clientId, setId);
    return store;
  };

  loadFromUpload = async (contents: FileContent[]): Promise<UserUploadData> => {
    /// Assumes > 1 file, processes just the first one loaded...
    return Promise.resolve(this.getAppUserUpload(contents[0]));
  };

  loadFromResultSetId = async (setId: DbKey<UserUploadSet>): Promise<Paginated<WithDbId<UserUploadRecord>>> => {
    if (this.authorized) {
      const loadedDataQuery = await this.transport.query(this.queryId(setId, 'uploadId'));
      return loadedDataQuery;
    }
    return Promise.reject('Not authorized');
  };

  loadFromResult = async (res: UserUploadResult): Promise<Paginated<WithDbId<UserUploadRecord>>> => {
    if (this.authorized) {
      if (res.result.kind === 'loaded') {
        const setId = res.set;
        const loadedDataQuery = await this.transport.query(this.queryId(setId, 'uploadId'));
        return loadedDataQuery;
      } else {
        return Promise.reject(`Upload did not succeed, status is ${res.result.kind}`);
      }
    }
    return Promise.reject('Not authorized');
  };

  loadClient = async (appClientId: DbKey<AppClient>): Promise<WithDbId<AppClient>> => {
    if (this.authorized) {
      const clientQuery = await this.transport.queryAppClient(this.queryId(appClientId));
      if (clientQuery.total_size === 1) {
        return clientQuery.items[0];
      } else {
        return Promise.reject(`Invalid AppClient Id ${appClientId}`);
      }
    }
    return Promise.reject('Not authorized');
  };

  @computed
  get loadingResult(): MaybeLoading<Paginated<WithDbId<UserUploadRecord>>> {
    if (this.authorized) {
      if (this.setId) {
        return fromPromise(this.loadFromResultSetId(this.setId));
      }
      return { kind: 'error', error: 'No UploadResultSet Id specified' };
    }
    return { kind: 'error', error: 'Not authorized' };
  }

  @computed
  get loadingClient(): MaybeLoading<WithDbId<AppClient>> {
    if (this.authorized) {
      return fromPromise(this.appClient);
    }
    return { kind: 'error', error: 'Not authorized' };
  }

  // Not async
  @action
  getAppUserUpload = (file: FileContent): UserUploadData => {
    // file.content defined as string | ArrayBuffer but it's a whole file...
    const upload = CSV.parse(file.content);
    /// Remove first line, and return the first element in it (?)
    const headerRow: string[] = upload.splice(0, 1)[0];

    /** Display information for Uploaded data */
    this.fields = headerRow.map((importField: string) => {
      const name = importField; //.toLowerCase().replace(/\W/g, "_")
      const label = importField.toUpperCase();
      return { name, label };
    });

    this.validateHeaderRow(this.fields);

    const uploadData: UserUploadRecord[] = upload.map((row: string[]) => {
      const {
        LASTNAME,
        FIRSTNAME,
        EMAIL,
        MANAGER_NAME,
        MANAGER_EMAIL,
        ASSESSMENT_1,
        ASSESSMENT_2,
        ASSESSMENT_3,
        USERGROUP_1,
        USERGROUP_2,
        USERGROUP_3,
        USERGROUP_4,
        USERGROUP_5
      } = this.userUploadFields;
      const item: UserUploadRecord = makeUserUploadRecord({
        uploadId: '',
        lastName: row[this.fields.findIndex(f => f.name === LASTNAME)],
        firstName: row[this.fields.findIndex(f => f.name === FIRSTNAME)],
        email: row[this.fields.findIndex(f => f.name === EMAIL)],
        managerName: row[this.fields.findIndex(f => f.name === MANAGER_NAME)],
        managerEmail: row[this.fields.findIndex(f => f.name === MANAGER_EMAIL)],
        appUserId: null,
        managerGroupId: null,
        memberId: null,
        managerId: null,
        managerAppUserId: null,
        quizTitlesEn: [
          row[this.fields.findIndex(f => f.name === ASSESSMENT_1)],
          row[this.fields.findIndex(f => f.name === ASSESSMENT_2)],
          row[this.fields.findIndex(f => f.name === ASSESSMENT_3)]
        ],
        quizIds: [],
        userQuizIds: [],
        userGroupNames: [
          row[this.fields.findIndex(f => f.name === USERGROUP_1)],
          row[this.fields.findIndex(f => f.name === USERGROUP_2)],
          row[this.fields.findIndex(f => f.name === USERGROUP_3)],
          row[this.fields.findIndex(f => f.name === USERGROUP_4)],
          row[this.fields.findIndex(f => f.name === USERGROUP_5)]
        ],
        userGroupIds: [],
        userGroupMemberIds: [],
        state: []
      });
      return item;
    });

    const uploadSet: UserUploadSet = makeUserUploadSet({
      clientId: this.appClientId,
      // // TODO: Default to English
      // lang: LangCode.en,
      filename: file.name,
      startTime: moment.now(),
      endTime: null,
      state: { kind: 'loading' }
    });

    return { set: uploadSet, list: uploadData };
  };

  validateHeaderRow = (fields: CSVField[]) => {
    /// Do't worry too much about the order, just ensure the fields are all there
    Object.values(this.userUploadFields).forEach(fieldName => {
      if (fields.find(field => field.name === fieldName) === undefined) {
        throw new Error(`FILEUPLOAD Field ${fieldName} not found in file`);
      }
    });
  };

  public processUpload = async (data: UserUploadData): Promise<UserUploadResult> => {
    if (this.authorized) {
      const result = await this.transport.processUpload(data);
      return result;
    }
    return Promise.reject('Not authorized');
  };
}



// WEBPACK FOOTER //
// ./src/admin/stores/appuser-upload-store.ts